import { dataLayer } from "@repo/analytics/data-layer-utils"; // Adjust if dataLayer is elsewhere
import {
  componentNameConstants,
  dlElementConstants,
  dlEventConstants,
  dlCtaTextConstants,
} from "@repo/analytics/data-layer.constants";

/**
 * Safely calculate the percentage width between low and high values.
 * Handles division by zero when high equals low by returning 50 as fallback.
 * 
 * @param price - Current price (number or string)
 * @param lowValue - Minimum value in range (number or string)
 * @param highValue - Maximum value in range (number or string)
 * @returns Percentage value clamped between 0 and 100
 * 
 * @example
 * calculateProgressWidth(125, 100, 150) // Returns 50 (midpoint)
 * calculateProgressWidth(100, 100, 100) // Returns 50 (equal values)
 * calculateProgressWidth(50, 100, 150)  // Returns 0 (clamped)
 * calculateProgressWidth(200, 100, 150) // Returns 100 (clamped)
 */
export const calculateProgressWidth = (
  price: number | string,
  lowValue: number | string,
  highValue: number | string,
): number => {
  const priceNum = Number(price);
  const lowNum = Number(lowValue);
  const highNum = Number(highValue);

  // Handle division by zero when high equals low
  if (highNum === lowNum) {
    return 50; // Default to middle position when range is zero
  }

  const percentage = (100 * (priceNum - lowNum)) / (highNum - lowNum);
  
  // Clamp between 0 and 100
  return Math.max(0, Math.min(100, percentage));
};

/**
 * Triggers data layer event for the Info Icon click in the Stock Fundamentals section.
 * @param sectionTitle - The title of the section where the icon was clicked.
 */
export const handleInfoIconDL = (loginStatus: string, sectionTitle: string) => {
  const data = {
    section_title: sectionTitle,
    component: componentNameConstants.STOCK_PERFORMANCE,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.ICON_CLICK,
    cta_text: dlElementConstants.OPEN_ICON,
    element: dlElementConstants.INFO_ICON,
  };

  const cleverTapObj = {
    EVENT_NAME: dlElementConstants.PDP_CLICKED,
    EP_ARTICLE_NAME: dlElementConstants.STOCK_PERFORMANCE,
  };

  dataLayer(loginStatus, data, cleverTapObj);
};

/**
 * Triggers data layer event when the close icon or button is clicked in Stock Fundamentals section.
 * @param sectionTitle - The title of the section where the event happened.
 * @param ctaText - The CTA label to send.
 * @param btnClick - If true, indicates a button click; otherwise, an icon click.
 */
export const handleCloseBtnDL = (
  loginStatus: string,
  sectionTitle: string,
  ctaText: string,
  btnClick = false,
) => {
  const data = {
    section_title: sectionTitle,
    component: componentNameConstants.STOCK_PERFORMANCE,
    event: dlEventConstants.CLICK,
    event_type: btnClick
      ? dlEventConstants.BUTTON_CLICK
      : dlEventConstants.ICON_CLICK,
    element: btnClick
      ? dlElementConstants.SCRIP_FINANCIALS_BTN_CLICK
      : dlElementConstants.SCRIP_FINANCIALS_ICON_CLICK,
    cta_text: ctaText,
  };

  const cleverTapObj = {
    EVENT_NAME: dlElementConstants.PDP_CLICKED,
    EP_ARTICLE_NAME: dlElementConstants.STOCK_PERFORMANCE,
    EP_CTA: dlCtaTextConstants.CLOSE_ICON,
  };

  dataLayer(loginStatus, data, cleverTapObj);
};
